import { getUserConfiguration } from 'owa-session-store';
import getUserConfigurationTabFromInboxClassificationFilter from '../selectors/getUserConfigurationTabFromInboxClassificationFilter';
import { bootstrapInboxPlusPivots } from 'owa-mail-inbox-plus-store';
import { orchestrator } from 'satcheljs';
import initializeInboxPlus from '../actions/initializeInboxPlus';
import createDropViewState from 'owa-dnd/lib/utils/createDropViewState';
import { isFocusedInboxEnabled } from 'owa-mail-list-actions/lib/utils/FocusedInboxUtil';
import { trace } from 'owa-trace';
import InboxClassificationFilter from 'owa-mail-inbox-plus-store/lib/store/schema/InboxClassificationFilter';
import InboxPlusPivotViewState from 'owa-mail-inbox-plus-store/lib/store/schema/InboxPlusPivotViewState';
import { isFeatureEnabled } from 'owa-feature-flags';

const allPivotTypes = [
    InboxClassificationFilter.None,
    InboxClassificationFilter.Focused,
    InboxClassificationFilter.Promotions,
    InboxClassificationFilter.Social,
    InboxClassificationFilter.Newsletters,
    InboxClassificationFilter.Other,
];

orchestrator(initializeInboxPlus, actionMessage => {
    const pivotsToBootstrap: InboxPlusPivotViewState[] = [];
    const userOptions = getUserConfiguration().UserOptions;
    const isFirstRunExperience = !userOptions.InboxPlusTabs;
    let didFixPivotState = false;

    // If the property is missing in sessionData, the user is loading inbox plus for the first time
    if (isFirstRunExperience) {
        const focusedInboxEnabled =
            userOptions.IsFocusedInboxFeatureSupported && userOptions.IsFocusedInboxEnabled;

        // default pivot set depends on whether focused inbox is enabled
        for (const pivot of allPivotTypes) {
            let isPivotEnabled;
            switch (pivot) {
                case InboxClassificationFilter.None:
                    isPivotEnabled = !focusedInboxEnabled;
                    break;
                case InboxClassificationFilter.Focused:
                case InboxClassificationFilter.Other:
                    isPivotEnabled = focusedInboxEnabled;
                    break;
                default:
                    if (isFeatureEnabled('tri-inboxPlusEasyOn')) {
                        isPivotEnabled = false;
                    } else {
                        isPivotEnabled = true;
                    }
                    break;
            }
            addInboxPlusPivot(pivotsToBootstrap, pivot, isPivotEnabled);
        }
    } else {
        // If the tabs are in session data, read them
        const userConfigurationPivots = userOptions.InboxPlusTabs;

        for (const pivotType of allPivotTypes) {
            addInboxPlusPivot(
                pivotsToBootstrap,
                pivotType,
                userConfigurationPivots.indexOf(
                    getUserConfigurationTabFromInboxClassificationFilter(pivotType)
                ) > -1
            );
        }
        // If verifyAndFixPivotStates returns true, we reset the user's pivots to a valid state and need to update
        // user configuration along with updating the local store.
        didFixPivotState = verifyAndFixPivotStates(pivotsToBootstrap);
    }
    bootstrapInboxPlusPivots(pivotsToBootstrap, isFirstRunExperience, didFixPivotState);
});

function addInboxPlusPivot(
    pivotsToBootstrap: InboxPlusPivotViewState[],
    pivotType: InboxClassificationFilter,
    isEnabled: boolean
) {
    pivotsToBootstrap.push({
        InboxClassificationFilter: pivotType,
        isEnabled: isEnabled,
        drop: createDropViewState(),
        recentSenders: [],
        unseenCount: 0,
    });
}

// Checks if pivots from user configuration are in a valid state. If not, this function modifies the values
// and returns true if modification happened
function verifyAndFixPivotStates(pivotsToVerify: InboxPlusPivotViewState[]) {
    const focusOtherEnabled = isFocusedInboxEnabled();
    const focusOtherPivots = pivotsToVerify.filter(
        pivot =>
            (pivot.InboxClassificationFilter === InboxClassificationFilter.Focused ||
                pivot.InboxClassificationFilter === InboxClassificationFilter.Other) &&
            pivot.isEnabled == true
    );
    const inboxPivot = pivotsToVerify.filter(
        pivot =>
            pivot.InboxClassificationFilter === InboxClassificationFilter.None &&
            pivot.isEnabled == true
    );
    // Valid state is either F/O OR Inbox enabled
    if (
        (focusOtherPivots.length == 2 && inboxPivot.length == 0 && focusOtherEnabled) ||
        (focusOtherPivots.length == 0 && inboxPivot.length == 1 && !focusOtherEnabled)
    ) {
        return false;
    }

    trace.warn(
        `Invalid inbox plus state. FocusedInboxEnabled: ${focusOtherEnabled}. Pivots: ${pivotsToVerify}`
    );
    // Set enabled pivots to match the focus/other setting in session data
    fixFocusOtherInboxPivots(pivotsToVerify, focusOtherEnabled);
    return true;
}

function fixFocusOtherInboxPivots(
    pivotsToFix: InboxPlusPivotViewState[],
    shouldEnableFocusedOther: boolean
) {
    for (const pivot of pivotsToFix) {
        const classificationFilter = pivot.InboxClassificationFilter;
        if (classificationFilter === InboxClassificationFilter.None) {
            pivot.isEnabled = !shouldEnableFocusedOther;
        } else if (
            classificationFilter === InboxClassificationFilter.Focused ||
            classificationFilter === InboxClassificationFilter.Other
        ) {
            pivot.isEnabled = shouldEnableFocusedOther;
        }
    }
}
